import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  InformationCircleIcon,
  DocumentPlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { SafeHtmlContent } from '../components/SafeHtmlContent';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUser } from '../contexts/UserContext';
import {
  checkSilentAuth,
  connectInteractive,
  getCurrentSecureScore,
  getScoreHistory,
  getControlProfiles,
  getSubscribedSkus,
  buildEnabledServicePlans,
  isControlApplicable,
  needsLicence,
  getScoreChange,
} from '../services/m365SecureScoreService';

// ── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ['Identity', 'Data', 'Device', 'Apps', 'Infrastructure'];

// Graph API returns camelCase implementationStatus values.
// Labels match what the Microsoft 365 Security Center portal displays.
const STATUS_LABELS = {
  notImplemented:        'To address',
  implemented:           'Completed',
  alternativeMitigation: 'Alternate mitigation',
  riskAccepted:          'Risk accepted',
  thirdParty:            'Third party',
  planned:               'Planned',
};

const CATEGORY_COLORS = {
  Identity:       'bg-purple-100 text-purple-800',
  Data:           'bg-blue-100 text-blue-800',
  Device:         'bg-green-100 text-green-800',
  Apps:           'bg-orange-100 text-orange-800',
  Infrastructure: 'bg-gray-100 text-gray-700',
};

const EFFORT_COLORS = {
  Low:      'bg-green-100 text-green-800',
  Moderate: 'bg-amber-100 text-amber-800',
  High:     'bg-red-100 text-red-800',
};

const IMPACT_COLORS = {
  Low:      'bg-sky-100 text-sky-800',
  Moderate: 'bg-amber-100 text-amber-800',
  High:     'bg-red-100 text-red-800',
};

const NO_LICENCE_CLASS = 'bg-slate-100 text-slate-500';

const TICKET_STATUS_BADGE = {
  New:             'bg-blue-100 text-blue-700 hover:bg-blue-200',
  InProgress:      'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
  WaitingFeedback: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  Closed:          'bg-gray-100 text-gray-500 hover:bg-gray-200',
  Reopened:        'bg-teal-100 text-teal-700 hover:bg-teal-200',
};

const TICKET_STATUS_LABEL = {
  New:             'New',
  InProgress:      'In Progress',
  WaitingFeedback: 'Waiting',
  Closed:          'Closed',
  Reopened:        'Reopened',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(pct) {
  if (pct >= 70) return '#10B981';
  if (pct >= 40) return '#F59E0B';
  return '#EF4444';
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function scoreImpact(control) {
  return Math.max(0, (control.maxScore ?? 0) - (control.currentScore ?? 0));
}

// Maps the raw Graph API state strings (PascalCase internal values) to the
// STATUS_LABELS keys used throughout this component.
// Maps raw Graph API state strings to STATUS_LABELS keys.
// "Default" is handled separately in getControlStatus — it maps to either
// 'notImplemented' or 'implemented' depending on whether currentScore >= maxScore.
const API_STATE_MAP = {
  Ignored:               'riskAccepted',
  ThirdParty:            'thirdParty',
  AlternateMitigation:   'alternativeMitigation',
  Planned:               'planned',
  Resolved:              'implemented',
  // defensive: camelCase variants
  notImplemented:        'notImplemented',
  implemented:           'implemented',
  thirdParty:            'thirdParty',
  alternativeMitigation: 'alternativeMitigation',
  riskAccepted:          'riskAccepted',
  planned:               'planned',
};

// Returns the effective portal status key for a control.
// implementationStatus is not returned by this Graph endpoint; status comes
// from controlStateUpdates.  "Default" is used for both "To address" and
// "Completed" — we distinguish them by comparing currentScore to maxScore.
function getControlStatus(c) {
  const updates = c.controlStateUpdates ?? [];
  const latest = updates.reduce((best, u) =>
    !best || new Date(u.updatedDateTime ?? 0) >= new Date(best.updatedDateTime ?? 0) ? u : best
  , null);
  const s = latest?.state;

  if (!s || s === 'noLicenseRequired' || s === 'unknownFutureValue') return 'notImplemented';

  if (s === 'Default') {
    const max = c.maxScore ?? 0;
    return (max > 0 && (c.currentScore ?? 0) >= max) ? 'implemented' : 'notImplemented';
  }

  return API_STATE_MAP[s] ?? 'notImplemented';
}

// ── Sub-components ───────────────────────────────────────────────────────────

// Circular arc progress ring (same pattern as SecureScore.jsx ScoreRing)
const ScoreRing = ({ pct, size = 130 }) => {
  const r = 42;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, pct)) / 100) * circ;
  const color = scoreColor(pct);
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="10" />
      <circle
        cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 50 50)"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x="50" y="47" textAnchor="middle" fontSize="18" fontWeight="bold" fill={color}>
        {Math.round(pct)}%
      </text>
      <text x="50" y="60" textAnchor="middle" fontSize="9" fill="#6B7280">score</text>
    </svg>
  );
};

const Badge = ({ label, colorClass, size = 'sm' }) => {
  const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center ${padding} rounded font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

// ── SecureScoreHeader ────────────────────────────────────────────────────────

const SecureScoreHeader = ({ currentScore, history }) => {
  const pct = currentScore?.maxScore > 0
    ? (currentScore.currentScore / currentScore.maxScore) * 100
    : 0;

  const change7  = getScoreChange(history, 7);
  const change30 = getScoreChange(history, 30);

  const benchmarks = currentScore?.averageComparativeScores ?? [];
  const industry   = benchmarks.find(b => b.basis === 'AllTenants' || b.basis === 'Industry');
  const seats      = benchmarks.find(b => b.basis === 'SeatSizeAll' || b.basis === 'SeatSize');

  const TrendBadge = ({ days, change }) => {
    if (change === null) return null;
    const positive = change >= 0;
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {positive ? '+' : ''}{change}pts {days}d
      </span>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
      <ScoreRing pct={Math.round(pct * 10) / 10} size={130} />

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-4xl font-bold text-gray-900" style={{ color: scoreColor(pct) }}>
            {Math.round(currentScore?.currentScore ?? 0)}
          </span>
          <span className="text-xl text-gray-400 font-medium">
            / {Math.round(currentScore?.maxScore ?? 0)}
          </span>
          <span className="text-sm text-gray-500">points</span>
        </div>

        <p className="text-sm text-gray-400 mt-1">
          as of {fmtDate(currentScore?.createdDateTime)}
        </p>

        <div className="flex flex-wrap gap-2 mt-3">
          <TrendBadge days={7}  change={change7}  />
          <TrendBadge days={30} change={change30} />
        </div>

        {(industry || seats) && (
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
            {industry && (
              <span>
                Industry avg:{' '}
                <span className="font-medium text-gray-700">
                  {Math.round(industry.averageScore ?? 0)}%
                </span>
              </span>
            )}
            {seats && (
              <span>
                Similar orgs:{' '}
                <span className="font-medium text-gray-700">
                  {Math.round(seats.averageScore ?? 0)}%
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── SecureScoreTrendChart ────────────────────────────────────────────────────

const SecureScoreTrendChart = ({ history }) => {
  const [open, setOpen] = useState(false);

  const data = [...history]
    .reverse()
    .map(s => ({
      date: new Date(s.createdDateTime).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' }),
      pct: s.maxScore > 0 ? Math.round((s.currentScore / s.maxScore) * 100) : 0,
    }));

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          Score trend — last {history.length} days
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                tickLine={false}
                interval={Math.floor(data.length / 8)}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => `${v}%`}
              />
              <Tooltip
                formatter={(v) => [`${v}%`, 'Score']}
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
              />
              <Line
                type="monotone"
                dataKey="pct"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ── SecureScoreFilters ───────────────────────────────────────────────────────

const SecureScoreFilters = ({
  statusFilter, setStatusFilter,
  categoryFilter, setCategoryFilter,
  effortFilter, setEffortFilter,
  licenceFilter, setLicenceFilter,
  visibleCount,
}) => {
  const PillGroup = ({ label, options, value, onChange }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-gray-500 shrink-0">{label}</span>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
            value === opt.value
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg px-6 py-4 flex flex-col gap-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PillGroup
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'All', label: 'All' },
            ...Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
          ]}
        />
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
          {visibleCount} control{visibleCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="flex flex-wrap gap-4">
        <PillGroup
          label="Category"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[{ value: 'All', label: 'All' }, ...CATEGORIES.map(c => ({ value: c, label: c }))]}
        />
        <PillGroup
          label="Effort"
          value={effortFilter}
          onChange={setEffortFilter}
          options={[
            { value: 'All', label: 'All' },
            { value: 'Low',      label: 'Low' },
            { value: 'Moderate', label: 'Moderate' },
            { value: 'High',     label: 'High' },
          ]}
        />
        <PillGroup
          label="Has licence"
          value={licenceFilter}
          onChange={setLicenceFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'yes', label: 'Yes' },
            { value: 'no',  label: 'No' },
          ]}
        />
      </div>
    </div>
  );
};

// ── SecureScoreSummaryStrip ──────────────────────────────────────────────────

const SecureScoreSummaryStrip = ({ controls, categoryFilter, setCategoryFilter, licenceFilter }) => {
  const notImpl = controls.filter(c =>
    getControlStatus(c) === 'notImplemented' &&
    (licenceFilter === 'all' || (licenceFilter === 'yes') === !needsLicence(c))
  );
  const available = notImpl.reduce((s, c) => s + scoreImpact(c), 0);

  const byCat = CATEGORIES.map(cat => ({
    cat,
    count: notImpl.filter(c => c.controlCategory === cat).length,
    pts:   notImpl.filter(c => c.controlCategory === cat).reduce((s, c) => s + scoreImpact(c), 0),
  })).filter(x => x.count > 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <div className="bg-white shadow rounded-lg p-4 col-span-2 sm:col-span-1">
        <p className="text-xs text-gray-500">Available points</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">+{Math.round(available)}</p>
        <p className="text-xs text-gray-400 mt-0.5">{notImpl.length} open controls</p>
      </div>
      {byCat.map(({ cat, count, pts }) => (
        <button
          key={cat}
          onClick={() => setCategoryFilter(categoryFilter === cat ? 'All' : cat)}
          className={`bg-white shadow rounded-lg p-4 text-left transition-all hover:shadow-md ${
            categoryFilter === cat ? 'ring-2 ring-blue-500' : ''
          }`}
        >
          <p className="text-xs text-gray-500">{cat}</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{count}</p>
          <p className="text-xs text-gray-400 mt-0.5">+{Math.round(pts)} pts</p>
        </button>
      ))}
    </div>
  );
};

// ── ScoreControlDetailPanel ──────────────────────────────────────────────────

const ScoreControlDetailPanel = ({ control, existingTicketId, onClose, onTicketLinked }) => {
  const open = !!control;
  const [ticketStatus, setTicketStatus] = useState('idle'); // idle | creating | created | error
  const [newTicketId, setNewTicketId]   = useState(null);
  const [ticketError, setTicketError]   = useState(null);

  // Reset local ticket state whenever a new control is opened
  useEffect(() => {
    setTicketStatus('idle');
    setNewTicketId(null);
    setTicketError(null);
  }, [control?.id]);

  // The ticket to display: prefer a freshly created one, fall back to the persistent link
  const displayTicketId = newTicketId ?? existingTicketId ?? null;

  const handleCreateTicket = async () => {
    setTicketStatus('creating');
    setTicketError(null);
    try {
      const categories = await apiService.getCategories();
      const securityCat = categories.find(c => c.name?.toLowerCase() === 'security');
      if (!securityCat) throw new Error('No "Security" category found. Please create one in system settings.');

      const description = control.remediation ||
        `Microsoft 365 Secure Score recommendation: ${control.title}`;

      const ticket = await apiService.createTicket({
        title: control.title,
        description,
        categoryId: securityCat.id,
        priority: 2,
      });
      const ticketId = ticket?.id ?? ticket?.ticketId ?? null;
      setNewTicketId(ticketId);
      setTicketStatus('created');

      // Persist the link so other admins see it too
      if (ticketId) {
        try {
          await apiService.saveSecureScoreTicketLink(control.id, ticketId);
          onTicketLinked?.(control.id, ticketId);
        } catch { /* link save failure is non-critical */ }
      }
    } catch (err) {
      setTicketError(err.message ?? 'Failed to create ticket.');
      setTicketStatus('error');
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[500px] bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {control && (
          <>
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
              <div className="pr-4">
                <h2 className="text-base font-semibold text-gray-900 leading-snug">
                  {control.title}
                </h2>
                {needsLicence(control) && (
                  <span className={`mt-1.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${NO_LICENCE_CLASS}`}>
                    Requires licence purchase
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Meta grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Score impact', value: `+${scoreImpact(control)} pts`, bold: true },
                  { label: 'Current score', value: `${control.currentScore ?? 0} / ${control.maxScore ?? 0}` },
                  {
                    label: 'Category',
                    value: (
                      <Badge label={control.controlCategory} colorClass={CATEGORY_COLORS[control.controlCategory] ?? 'bg-gray-100 text-gray-700'} />
                    ),
                  },
                  {
                    label: 'Effort',
                    value: control.implementationCost
                      ? <Badge label={control.implementationCost} colorClass={EFFORT_COLORS[control.implementationCost] ?? 'bg-gray-100 text-gray-700'} />
                      : '—',
                  },
                  {
                    label: 'User impact',
                    value: control.userImpact
                      ? <Badge label={control.userImpact} colorClass={IMPACT_COLORS[control.userImpact] ?? 'bg-gray-100 text-gray-700'} />
                      : '—',
                  },
                  { label: 'Tier', value: control.tier ?? '—' },
                  { label: 'Service', value: control.service ?? '—' },
                  { label: 'Action type', value: control.actionType ?? '—' },
                ].map(({ label, value, bold }) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    {typeof value === 'string' ? (
                      <p className={`text-sm ${bold ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{value}</p>
                    ) : value}
                  </div>
                ))}
              </div>

              {/* Remediation */}
              {control.remediation && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Remediation</h3>
                  <SafeHtmlContent
                    className="text-sm text-gray-600 leading-relaxed
                      [&_p]:mb-2 [&_p:last-child]:mb-0
                      [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-1
                      [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
                      [&_li]:leading-snug
                      [&_strong]:font-semibold [&_strong]:text-gray-800
                      [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
                    html={control.remediation}
                  />
                </div>
              )}

              {/* Remediation impact */}
              {control.remediationImpact && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Remediation impact</h3>
                  <SafeHtmlContent
                    className="text-sm text-gray-600 leading-relaxed
                      [&_p]:mb-2 [&_p:last-child]:mb-0
                      [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_ol]:space-y-1
                      [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
                      [&_li]:leading-snug
                      [&_strong]:font-semibold [&_strong]:text-gray-800
                      [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
                    html={control.remediationImpact}
                  />
                </div>
              )}

              {/* Threats */}
              {control.threats?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Threats addressed</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {control.threats.map(t => (
                      <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Propagation notice */}
              <div className="flex items-start gap-2 text-xs text-gray-400 bg-blue-50 rounded-lg p-3">
                <InformationCircleIcon className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                Score changes typically reflect within 24–48 hours of implementation.
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-3">
              {/* Ticket feedback */}
              {(ticketStatus === 'created' || existingTicketId) && displayTicketId && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-500" />
                  <span>{ticketStatus === 'created' ? 'Ticket created' : 'Ticket already exists'}</span>
                  <Link
                    to={`/ticket/${displayTicketId}`}
                    onClick={onClose}
                    className="ml-auto text-xs text-green-700 underline hover:text-green-900 font-medium"
                  >
                    View ticket #{displayTicketId}
                  </Link>
                </div>
              )}
              {ticketStatus === 'error' && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
                  <span>{ticketError}</span>
                </div>
              )}

              <div className="flex gap-2">
                {/* Create / view ticket */}
                {existingTicketId && ticketStatus === 'idle' ? (
                  <Link
                    to={`/ticket/${existingTicketId}`}
                    onClick={onClose}
                    className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircleIcon className="h-4 w-4" />
                    View ticket #{existingTicketId}
                  </Link>
                ) : (
                  <button
                    onClick={handleCreateTicket}
                    disabled={ticketStatus === 'creating' || ticketStatus === 'created'}
                    className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {ticketStatus === 'creating' ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : ticketStatus === 'created' ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <DocumentPlusIcon className="h-4 w-4" />
                    )}
                    {ticketStatus === 'creating' ? 'Creating…' : ticketStatus === 'created' ? 'Ticket created' : 'Create ticket'}
                  </button>
                )}

                {/* Open in portal */}
                {control.actionUrl && (
                  <a
                    href={control.actionUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    title="Open in Microsoft 365 Security Center"
                  >
                    Open
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ── ScoreControlRow ──────────────────────────────────────────────────────────

const ScoreControlRow = ({ control, rank, onSelect, ticketLink }) => {
  const impact = scoreImpact(control);
  const noLicence = needsLicence(control);
  const statusLabel = ticketLink?.ticketStatus ? TICKET_STATUS_LABEL[ticketLink.ticketStatus] ?? ticketLink.ticketStatus : null;
  const badgeClass = ticketLink?.ticketStatus
    ? TICKET_STATUS_BADGE[ticketLink.ticketStatus] ?? 'bg-green-100 text-green-700 hover:bg-green-200'
    : 'bg-green-100 text-green-700 hover:bg-green-200';
  return (
    <tr
      onClick={() => onSelect(control)}
      className="hover:bg-blue-50 cursor-pointer transition-colors group"
    >
      <td className="px-4 py-3 text-xs text-gray-400 w-8 text-right">{rank}</td>
      <td className="px-4 py-3 text-sm text-gray-900 font-medium leading-snug">
        <span className="mr-2">{control.title}</span>
        {noLicence && (
          <Badge label="No licence" colorClass={NO_LICENCE_CLASS} size="xs" />
        )}
        {ticketLink?.ticketId && (
          <Link
            to={`/ticket/${ticketLink.ticketId}`}
            onClick={e => e.stopPropagation()}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${badgeClass}`}
            title={`Ticket #${ticketLink.ticketId}${statusLabel ? ` · ${statusLabel}` : ''}`}
          >
            <DocumentPlusIcon className="h-3 w-3" />
            #{ticketLink.ticketId}
            {statusLabel && <span className="opacity-75">· {statusLabel}</span>}
          </Link>
        )}
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <Badge
          label={control.controlCategory}
          colorClass={CATEGORY_COLORS[control.controlCategory] ?? 'bg-gray-100 text-gray-700'}
        />
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-bold" style={{ color: scoreColor(Math.min(100, impact * 4)) }}>
          +{impact} pts
        </span>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        {control.implementationCost ? (
          <Badge
            label={control.implementationCost}
            colorClass={EFFORT_COLORS[control.implementationCost] ?? 'bg-gray-100 text-gray-700'}
          />
        ) : <span className="text-gray-300 text-xs">—</span>}
      </td>
      <td className="px-4 py-3 hidden lg:table-cell">
        {control.userImpact ? (
          <Badge
            label={control.userImpact}
            colorClass={IMPACT_COLORS[control.userImpact] ?? 'bg-gray-100 text-gray-700'}
          />
        ) : <span className="text-gray-300 text-xs">—</span>}
      </td>
      <td className="px-4 py-3 text-right w-10">
        {control.actionUrl && (
          <a
            href={control.actionUrl}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-700"
            title="Open in Microsoft 365 Security Center"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        )}
      </td>
    </tr>
  );
};

// ── ScoreControlCategory ─────────────────────────────────────────────────────

const ScoreControlCategory = ({ category, controls, onSelect, ticketLinks }) => {
  const [open, setOpen] = useState(true);
  const available = controls.reduce((s, c) => s + scoreImpact(c), 0);

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
          <span className="font-semibold text-gray-900">{category}</span>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {controls.length}
          </span>
        </div>
        <span className="text-sm font-medium text-gray-500">+{Math.round(available)} pts available</span>
      </button>

      {open && controls.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-t border-gray-100">
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-right w-8">#</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-left">Control</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-left hidden md:table-cell">Category</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-right">Impact</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-left hidden sm:table-cell">Effort</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-400 text-left hidden lg:table-cell">User impact</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {controls.map((c, i) => (
                <ScoreControlRow key={c.id} control={c} rank={i + 1} onSelect={onSelect} ticketLink={ticketLinks?.[c.id]} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── LicenceFilterNotice ──────────────────────────────────────────────────────

const LicenceFilterNotice = ({ hiddenCount, skusError }) => {
  if (skusError) {
    return (
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        <span>
          Licence data unavailable — showing all controls without licence filtering.
          Controls that require unlicensed services may be included.
        </span>
      </div>
    );
  }
  if (hiddenCount <= 0) return null;
  return (
    <p className="text-xs text-gray-400 text-center py-2">
      {hiddenCount} control{hiddenCount !== 1 ? 's' : ''} excluded — zero-score or unlicensed service not in your tenant.
    </p>
  );
};

// ── ConnectPanel ─────────────────────────────────────────────────────────────

const ConnectPanel = ({ onConnect, connecting, error }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-6 text-center max-w-md mx-auto">
    <div className="p-4 bg-blue-50 rounded-full">
      <ShieldCheckIcon className="h-10 w-10 text-blue-600" />
    </div>
    <div>
      <h2 className="text-lg font-semibold text-gray-900">Connect to Microsoft 365</h2>
      <p className="text-sm text-gray-500 mt-2">
        Sign in to load your Microsoft Secure Score and security control recommendations.
        Requires <span className="font-medium">SecurityEvents.Read.All</span> permission.
      </p>
    </div>
    {error && (
      <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 text-left w-full">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{error}</span>
      </div>
    )}
    <button
      onClick={onConnect}
      disabled={connecting}
      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
    >
      {connecting ? (
        <ArrowPathIcon className="h-4 w-4 animate-spin" />
      ) : (
        <ShieldCheckIcon className="h-4 w-4" />
      )}
      {connecting ? 'Connecting…' : 'Connect to Microsoft 365'}
    </button>
  </div>
);

// ── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="bg-white shadow rounded-lg p-6 h-32 flex items-center gap-6">
      <div className="h-28 w-28 rounded-full bg-gray-200" />
      <div className="flex-1 space-y-3">
        <div className="h-8 bg-gray-200 rounded w-32" />
        <div className="h-4 bg-gray-100 rounded w-48" />
        <div className="h-4 bg-gray-100 rounded w-36" />
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-white shadow rounded-lg p-4 h-20 bg-gray-50" />
      ))}
    </div>
    {[...Array(3)].map((_, i) => (
      <div key={i} className="bg-white shadow rounded-lg p-6 h-24 bg-gray-50" />
    ))}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function M365SecureScore() {
  const { isAdmin } = useUser();

  const [connected, setConnected]   = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connError, setConnError]   = useState(null);

  const [loading, setLoading]             = useState(false);
  const [currentScore, setCurrentScore]   = useState(null);
  const [scoreHistory, setScoreHistory]   = useState([]);
  const [controls, setControls]           = useState([]);      // all applicable controls
  const [hiddenCount, setHiddenCount]     = useState(0);
  const [skusError, setSkusError]         = useState(null);
  const [fetchError, setFetchError]       = useState(null);
  const [profilesWarning, setProfilesWarning] = useState(null);
  // Map of controlId → ticketId for controls that already have a ticket
  const [ticketLinks, setTicketLinks]     = useState({});

  const [statusFilter,   setStatusFilter]   = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [effortFilter,   setEffortFilter]   = useState('All');
  const [licenceFilter,  setLicenceFilter]  = useState('yes');
  const [selectedControl, setSelectedControl] = useState(null);

  // Try silent auth on mount
  useEffect(() => {
    checkSilentAuth()
      .then(() => setConnected(true))
      .catch(() => { /* needs interactive */ });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setSkusError(null);
    setProfilesWarning(null);

    const [scoreRes, historyRes, profilesRes, skusRes] = await Promise.allSettled([
      getCurrentSecureScore(),
      getScoreHistory(),
      getControlProfiles(),
      getSubscribedSkus(),
    ]);

    if (scoreRes.status === 'rejected') {
      setFetchError(scoreRes.reason?.message ?? 'Failed to load secure score.');
      setLoading(false);
      return;
    }

    const rawScore    = scoreRes.value;
    const rawHistory  = historyRes.status === 'fulfilled' ? historyRes.value : [];
    const rawProfiles = profilesRes.status === 'fulfilled' ? profilesRes.value : [];

    if (profilesRes.status === 'rejected') {
      setProfilesWarning(profilesRes.reason?.message ?? 'Failed to load control profiles.');
    }

    let enabledPlans = new Set();
    if (skusRes.status === 'fulfilled') {
      enabledPlans = buildEnabledServicePlans(skusRes.value);
    } else {
      setSkusError(skusRes.reason?.message ?? 'Failed to load licence data.');
    }

    // Enrich profiles with currentScore from the score snapshot so that
    // getControlStatus can distinguish "Default+completed" from "Default+to-address".
    // Build a map of controlName → score from the tenant's score snapshot.
    // Controls that are absent from controlScores are unlicensed — they appear
    // in the profiles endpoint but don't contribute to this tenant's score.
    const scoreMap = {};
    for (const cs of rawScore?.controlScores ?? []) {
      if (cs.controlName) scoreMap[cs.controlName] = cs.score ?? 0;
    }
    const enriched = rawProfiles.map(c => ({
      ...c,
      currentScore: scoreMap[c.id] ?? 0,
      hasLicence: Object.prototype.hasOwnProperty.call(scoreMap, c.id),
    }));

    // Apply filtering and count hidden
    const applicable = enriched.filter(c =>
      isControlApplicable(c, c.controlStateUpdates ?? [], enabledPlans)
    );
    setHiddenCount(enriched.length - applicable.length);

    console.log('[M365] profiles fetched:', rawProfiles.length, '| applicable:', applicable.length);

    // Load existing ticket links (fire-and-forget — don't block on failure)
    try {
      const links = await apiService.getSecureScoreTicketLinks();
      const map = {};
      for (const l of links ?? []) map[l.controlId] = { ticketId: l.ticketId, ticketStatus: l.ticketStatus };
      setTicketLinks(map);
    } catch { /* non-critical */ }

    setCurrentScore(rawScore);
    setScoreHistory(rawHistory);
    setControls(applicable);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (connected) loadData();
  }, [connected, loadData]);

  const handleConnect = async () => {
    setConnecting(true);
    setConnError(null);
    try {
      await connectInteractive();
      setConnected(true);
    } catch (err) {
      setConnError(err.message ?? 'Failed to connect. Ensure SecurityEvents.Read.All permission is granted in Entra ID.');
    } finally {
      setConnecting(false);
    }
  };

  if (!isAdmin()) return null;

  // ── Filtering & grouping ──────────────────────────────────────────────────

  const filtered = controls.filter(c => {
    if (statusFilter !== 'All' && getControlStatus(c) !== statusFilter) return false;
    if (categoryFilter !== 'All' && c.controlCategory !== categoryFilter) return false;
    if (effortFilter !== 'All' && c.implementationCost !== effortFilter) return false;
    if (licenceFilter === 'yes' && needsLicence(c)) return false;
    if (licenceFilter === 'no'  && !needsLicence(c)) return false;
    return true;
  });

  // Sort by score impact desc within each category
  const sorted = [...filtered].sort((a, b) => scoreImpact(b) - scoreImpact(a));

  const grouped = CATEGORIES.map(cat => ({
    cat,
    items: sorted.filter(c => c.controlCategory === cat),
  })).filter(g => g.items.length > 0);

  // Catch-all for controls with non-standard or missing category
  const knownCats = new Set(CATEGORIES);
  const other = sorted.filter(c => !knownCats.has(c.controlCategory));
  if (other.length > 0) grouped.push({ cat: 'Other', items: other });

  const isEmpty = grouped.length === 0 && !loading;
  const allImplemented =
    isEmpty &&
    (statusFilter === 'All' || statusFilter === 'notImplemented') &&
    controls.filter(c => getControlStatus(c) === 'notImplemented').length === 0 &&
    controls.length > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-7 w-7 text-blue-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Microsoft 365 Secure Score</h1>
            <p className="text-sm text-gray-500">Microsoft Security Center posture and recommendations</p>
          </div>
        </div>
        {connected && (
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Not connected */}
      {!connected && (
        <ConnectPanel onConnect={handleConnect} connecting={connecting} error={connError} />
      )}

      {/* Loading */}
      {connected && loading && <LoadingSkeleton />}

      {/* Error */}
      {connected && !loading && fetchError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-5 py-4 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Failed to load Microsoft 365 Secure Score</p>
            <p className="mt-1 text-red-600">{fetchError}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      {connected && !loading && !fetchError && (
        <div className="space-y-4">
          {/* Score header */}
          {currentScore && (
            <SecureScoreHeader currentScore={currentScore} history={scoreHistory} />
          )}

          {/* Trend chart */}
          {scoreHistory.length > 1 && <SecureScoreTrendChart history={scoreHistory} />}

          {/* Filters */}
          <SecureScoreFilters
            statusFilter={statusFilter}   setStatusFilter={setStatusFilter}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            effortFilter={effortFilter}   setEffortFilter={setEffortFilter}
            licenceFilter={licenceFilter}  setLicenceFilter={setLicenceFilter}
            visibleCount={filtered.length}
          />

          {/* Control profiles error */}
          {profilesWarning && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 text-sm text-amber-800">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
              <div>
                <p className="font-medium">Control profiles could not be loaded</p>
                <p className="mt-1 text-amber-700">{profilesWarning}</p>
              </div>
            </div>
          )}

          {/* Summary strip — show for All and notImplemented views */}
          {(statusFilter === 'All' || statusFilter === 'notImplemented') && controls.length > 0 && (
            <SecureScoreSummaryStrip
              controls={controls}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              licenceFilter={licenceFilter}
            />
          )}

          {/* All implemented success state */}
          {allImplemented && (
            <div className="flex flex-col items-center py-20 gap-4 text-center">
              <div className="p-4 bg-green-50 rounded-full">
                <ShieldCheckIcon className="h-10 w-10 text-green-500" />
              </div>
              <p className="text-lg font-semibold text-gray-800">All controls implemented</p>
              <p className="text-sm text-gray-500">Outstanding work — no open recommendations.</p>
            </div>
          )}

          {/* Filtered empty */}
          {isEmpty && !allImplemented && (
            <div className="flex flex-col items-center py-16 gap-3 text-center text-gray-400">
              <p className="text-sm">No controls match the current filters.</p>
              <button
                onClick={() => { setStatusFilter('All'); setCategoryFilter('All'); setEffortFilter('All'); setLicenceFilter('yes'); }}
                className="text-xs text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Control categories */}
          {grouped.map(({ cat, items }) => (
            <ScoreControlCategory
              key={cat}
              category={cat}
              controls={items}
              onSelect={setSelectedControl}
              ticketLinks={ticketLinks}
            />
          ))}

          {/* Licence notice */}
          <LicenceFilterNotice hiddenCount={hiddenCount} skusError={skusError} />
        </div>
      )}

      {/* Detail panel */}
      <ScoreControlDetailPanel
        control={selectedControl}
        existingTicketId={selectedControl ? ticketLinks[selectedControl.id]?.ticketId : null}
        onClose={() => setSelectedControl(null)}
        onTicketLinked={(controlId, ticketId) =>
          setTicketLinks(prev => ({ ...prev, [controlId]: { ticketId, ticketStatus: 'New' } }))
        }
      />
    </div>
  );
}
